import React, { useEffect, useMemo, useState } from "react"
import { Redirect, Route, Switch, useHistory } from "react-router-dom"
import { UserContext } from "./components/ProjectSpecificComponents/Contexts/UserContext"
import { IAD, IToken, IUser } from "./components/ProjectSpecificComponents/Interfaces/User"
import GlobalOverlayLoader from "./components/ProjectNonSpecificComponents/Elements/Loader/GlobalOverlayLoader"
import { observer } from "mobx-react"
import { useStore } from "./components/ProjectSpecificComponents/Contexts/GlobalStoreContext"

import AppInfoModal from "./components/ProjectSpecificComponents/Elements/Global/molecules/Modals/InfoModal/AppInfoModal"
import UserOverviewPage from "components/ProjectSpecificComponents/Templates/UserOverviewPage"
import NavBar from "components/ProjectSpecificComponents/Elements/Navigation/organisms/NavBar/NavBar"
import ConnectionPage from "components/ProjectSpecificComponents/Pages/ConnectionPage"
import { useFormStore } from "components/ProjectSpecificComponents/Contexts/FormStoreContext"
import AppRequests from "components/ProjectSpecificComponents/API/AppRequests/AppRequests"
import ROUTES from "components/ProjectSpecificComponents/Constants/ROUTES"
import IconsDevPage from "components/ProjectSpecificComponents/Pages/IconsDevPage"
import AdminLoginPage from "components/ProjectSpecificComponents/Pages/AdminLoginPage"
import FormConfigurationPage from "components/ProjectSpecificComponents/Pages/FormConfigurationPage"
import Footer from "components/ProjectSpecificComponents/Elements/Footer/Footer"
import { useLocation } from "react-router-dom"
import SettingsModal from "components/ProjectSpecificComponents/Elements/Global/molecules/Modals/SettingsModal"
import FormConfigurationOverviewTable from "components/ProjectSpecificComponents/Pages/FormConfigurationOverviewTable"
import i18n from "i18n"
import UnauthorizedAccessPage from "./components/ProjectSpecificComponents/Pages/UnauthorizedAccessPage"
import PTKMConfigurationOverviewTable from "components/ProjectSpecificComponents/Pages/PTKMConfigurationOverviewTable"
import PTKMFormConfigurationPage from "components/ProjectSpecificComponents/Pages/PTKMFormConfigurationPage"
import PTKMCalendarConfig from "components/ProjectSpecificComponents/Pages/PTKMCalendarConfig"
import getUrlToken from "./components/ProjectNonSpecificComponents/Functions/getUrlToken"
import { useTranslation } from "react-i18next"
import SessionTimeout from "components/ProjectSpecificComponents/Elements/Global/molecules/SessionTimeout"
import { lostOrFinishedUserSession } from "components/ProjectSpecificComponents/API/Functions/lostOrFinishedUserSession"
import { lostOrFinishedAdminSession } from "components/ProjectSpecificComponents/API/Functions/lostOrFinishedAdminSession"
import { ISettings } from "components/ProjectSpecificComponents/Interfaces/FormsInterface"

export const App = observer(() => {
	const store = useStore()
	const formStore = useFormStore()
	const [user, setUser] = useState<IUser>()
	const userMemo = useMemo(() => ({ user, setUser }), [user, setUser])
	const location = useLocation()
	const history = useHistory()
	const [token, setToken] = useState<string | undefined>(undefined)
	const { userHasAdminAccess, userIsAuthorized, hasAdminRoute, isIIS, isToken } = store
	const { t } = useTranslation()
	const [isLoading, setIsLoading] = useState<boolean>(true)

	const { instanceAnonymousUsers } = store

	useEffect(() => {
		// get admin network (if server restart first render get network also (as user can be in sessionStorage)
		getNetwork()
	}, [])

	useEffect(() => {
		if (
			hasAdminRoute &&
			userHasAdminAccess &&
			(location.pathname.includes(ROUTES.PTKM) || location.pathname.includes(ROUTES.PTKM_CALENDAR))
		) {
			fetchAdminSettings()
		}
	}, [hasAdminRoute, userHasAdminAccess])

	const fetchAdminSettings = async () => {
		const { ok, data, statusCode } = await AppRequests("Setting").get<ISettings>(
			"get-settings-admin"
		)
		store.triggerSessionTimeout()
		store.setLoaderOverlayOpen(false)

		if (ok && data) {
			formStore.mergeSettingsDataSafely(data)
		} else if (statusCode == 401) {
			lostOrFinishedAdminSession(history, store)
		} else {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "settings.failedTotal",
				descriptionKey: "settings.totalFail",
			})
		}
	}

	/** setup permision to admin routes */
	const getNetwork = async () => {
		const { ok, data } = await AppRequests("Admin").get<boolean>()
		if (ok && data) {
			store.setHasAdminRoute(true)
			// Check if user has admin access
			const hasAccess = sessionStorage.getItem("access") == "true"
			store.setUserHasAdminAccess(hasAccess)
		}
		setIsLoading(false)
	}

	const addAdmin = () => {
		// Check if user has admin access
		const hasAccess = sessionStorage.getItem("access") == "true"
		store.setUserHasAdminAccess(hasAccess)
	}

	useEffect(() => {
		async function tryFindUserMetadata() {
			const sessionUser = sessionStorage.getItem("user")

			if (instanceAnonymousUsers) {
				// extra loģika ja HD instance ir priekš anonimiem lietotajiem
				addAdmin()
			} else if (sessionUser) {
				setUser(JSON.parse(sessionUser))
				addAdmin()
			} else if (isIIS) {
				// operate only with active directory
				getUser()
			} else if (isToken) {
				// operate only with token (also in active directory can be set)
				const _token = getUrlToken()
				const sessionToken = sessionStorage.getItem("token")
				_token ? getUserFromToken(_token) : sessionToken ? getUserFromToken(sessionToken) : getAD()
				!token && setToken(_token)
			} else {
				// first render should get info about server authentification method
				getAD()
			}
		}
		/** Checks if user and correct auth route */
		tryFindUserMetadata()
	}, [location, store.sessionRelod, instanceAnonymousUsers])

	useEffect(() => {
		if (user) {
			sessionStorage.setItem("user", JSON.stringify(user))
		}
	}, [user])

	useEffect(() => {
		store.setIsInCnfMenu(
			location.pathname.includes(ROUTES.REQUEST_CNFG) || location.pathname.includes(ROUTES.PTKM)
		)

		store.setIsInAdminSection(
			location.pathname.includes(ROUTES.ADMIN_OVERVIEW) ||
				location.pathname.includes(ROUTES.REQUEST_CNFG) ||
				location.pathname.includes(ROUTES.PTKM) ||
				location.pathname.includes(ROUTES.CONNECT) ||
				location.pathname.includes(ROUTES.FORM_CONFIGURATION)
		)
	}, [location.pathname])

	const getAD = async () => {
		const { ok, data } = await AppRequests("Setting").get<IAD>("get-Auth-Type")

		if (ok && data) {
			if (data.isAnonymous) {
				store.setInstanceAnonymousUsers(true)
			} else if (data.isIIS) {
				store.setIsIIS(true)
				getUser()
			} else {
				// first render have url, second if needed will have state and session
				const _urlToken = getUrlToken()
				const sessionToken = sessionStorage.getItem("token")
				let _token = _urlToken ? _urlToken : token ? token : sessionToken
				if (!_token) {
					store.setLoaderOverlayOpen(false)
					store.setUserIsAuthorized(false)
					return
				}
				store.setIsToken(true)
				getUserFromToken(_token)
			}
		}
	}

	const getUser = async () => {
		store.setLoaderOverlayOpen(true)
		const { ok, data, statusCode } = await AppRequests("User").get<IUser>("get-active-user")
		
		if (ok && data) {
			setUser(data)
			// get admin permission after user set in BE
			getNetwork()
		} else if (statusCode == 401) {
			store.setUserIsAuthorized(false)
		}
		store.setLoaderOverlayOpen(false)
	}

	const getUserFromToken = async (token: string) => {
		store.setLoaderOverlayOpen(true)
		const { ok, data, statusCode } = await AppRequests("User").post<IToken, IUser>(
			"get-user-from-token",
			{ token }
		)
		
		if (ok && data) {
			sessionStorage.setItem("token", token)
			setToken(token)
			setUser(data)
			store.setUserIsAuthorized(true)
			// get admin permission after user set in BE
			getNetwork()
		} else if (statusCode == 401) {
			store.setUserIsAuthorized(false)
		}
		store.setLoaderOverlayOpen(false)
	}

	// Sets language from localStorage
	useEffect(() => {
		const localStorageLanguage = localStorage.getItem("language")
		if (!localStorageLanguage) return
		try {
			i18n.changeLanguage(JSON.parse(localStorageLanguage).value)
			formStore.setAllLanguages(JSON.parse(localStorageLanguage).value)
		} catch (err) {
			console.error("failed to set language from localStorage")
		}
	}, [])

	useEffect(() => {
		const handleSubNavText = () => {
			switch (true) {
				case location.pathname.includes(ROUTES.PTKM_CONFIGURATION):
					return <p>{t("button.PTKMConfig")}</p>
				case location.pathname.includes(ROUTES.FORM_CONFIGURATION):
					return <p>{t("button.requestConfig")}</p>
				default:
					return undefined
			}
		}

		store.setSubNavElement(handleSubNavText())
	}, [location, t])

	const autoLogout = () => {
		setUser(undefined)
		sessionStorage.clear()
		store.setUserIsAuthorized(false)
		store.setUserHasAdminAccess(false)
	}

	const manualLogout = () => {
		setUser(undefined)
		lostOrFinishedUserSession(store)
	}

	if (!userIsAuthorized && !store.isLoaderOverlayOpen && !instanceAnonymousUsers) {
		return <Route component={UnauthorizedAccessPage} />
	}

	return (
		<>
			<UserContext.Provider value={userMemo}>
				<GlobalOverlayLoader active={store.isLoaderOverlayOpen} />
				<AppInfoModal />

				{userHasAdminAccess && store.showAdminSettingsModal && (
					<SettingsModal
						onClose={() => {
							formStore.restorePreviouslySelectedColors()
							store.setShowAdminSettingsModal(false)
							store.resetAdminSettingsSection()
						}}
					/>
				)}

				{(user || instanceAnonymousUsers) && (
					<>
						<NavBar />
						<Switch>
							<Route exact path={ROUTES.USER} component={UserOverviewPage} />
							{hasAdminRoute && <Route path={ROUTES.ADMIN_OVERVIEW} component={AdminLoginPage} />}

							{hasAdminRoute && userHasAdminAccess && (
								<Route path={ROUTES.CONNECT} component={ConnectionPage} />
							)}
							{hasAdminRoute && userHasAdminAccess ? (
								<>
									<Route path={ROUTES.ADMIN_OVERVIEW} component={AdminLoginPage} />
									<Route path={ROUTES.CONNECT} component={ConnectionPage} />
									<Route path={ROUTES.PTKM} component={PTKMConfigurationOverviewTable} />
									<Route path={ROUTES.PTKM_CONFIGURATION} component={PTKMFormConfigurationPage} />
									<Route path={ROUTES.PTKM_CALENDAR} component={PTKMCalendarConfig} />
									<Route path={ROUTES.FORM_CONFIGURATION} component={FormConfigurationPage} />
									<Route path={ROUTES.REQUEST_CNFG} component={FormConfigurationOverviewTable} />
									<Route exact path={ROUTES.DEV_ICONS} component={IconsDevPage} />
								</>
							) : (
								!isLoading && <Redirect to={ROUTES.USER} />
							)}
						</Switch>
						<SessionTimeout
							isAnonymous={instanceAnonymousUsers}
							autoLogOut={autoLogout}
							manualLogOut={manualLogout}
						/>
					</>
				)}

				<Footer />
			</UserContext.Provider>
		</>
	)
})

export default App
